import React, { useCallback, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import get from 'lodash/get'
import {
  ControlledCheckbox,
  ControlledHiddenInput,
  ControlledSelect,
  ControlledTextInput,
} from 'transfix-form/components'
import ControlledAutocompleteCarrier from 'transfix-form/components/ControlledAutocompleteCarrier'
import ControlledAutocompleteShipper from 'transfix-form/components/ControlledAutocompleteShipper'
import { Box, Typography } from 'transfix-ui/core'
import {
  IConnectionConfigEnvironment,
  IConnectionTypes,
  IUsageIndicators,
} from 'types/graphqlTypes'
import { Button, ButtonGroup } from 'transfix-ui'
import { As2ConnectionConfigGrid } from '../../ConnectionConfigGrids/As2ConnectionConfigGrid'
import { FtpConnectionConfigGrid } from '../../ConnectionConfigGrids/FtpConnectionConfigGrid'
import { useEdiOutboundConnectionConfigModalFormContext } from '../hooks/useEdiOutboundConnectionConfigModalForm'
import { TEdiOutboundConnectionConfigModalSchema } from '../schema/EdiOutboundConnectionConfigModalSchema'

const getErrorMessage = <
  T extends FieldErrors<TEdiOutboundConnectionConfigModalSchema>,
  K extends keyof T
>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

const TEST_INDICATOR_OPTIONS = [
  {
    label: 'Production',
    value: IUsageIndicators.PRODUCTION,
  },
  {
    label: 'Test',
    value: IUsageIndicators.TEST,
  },
]

const CONNECTION_TYPE_OPTIONS = [
  {
    label: 'SFTP with Private Key',
    value: IConnectionTypes.SFTP_WITH_PRIVATE_KEY,
  },
  {
    label: 'SFTP with Password',
    value: IConnectionTypes.SFTP_WITH_PASSWORD,
  },
  {
    label: 'FTP',
    value: IConnectionTypes.FTP,
  },
  {
    label: 'AS2',
    value: IConnectionTypes.AS2,
  },
]

const OUTBOUND_CORE_TEXT_INPUT_ITEMS = [
  {
    label: 'Interchange Version',
    name: 'interchangeVersion',
    dataTest: 'interchange-version-field',
    defaultValue: '',
  },
  {
    label: 'Group Version',
    name: 'groupVersion',
    dataTest: 'group-version-field',
    defaultValue: '',
  },
  {
    label: 'ISA Receiver ID',
    name: 'isaReceiverId',
    dataTest: 'isa-receiver-id-field',
    defaultValue: '',
  },
  {
    label: 'ISA Receiver ID Qualifier',
    name: 'isaReceiverIdQualifier',
    dataTest: 'isa-receiver-id-qualifier-field',
    defaultValue: '',
  },
  {
    label: 'ISA Sender ID',
    name: 'isaSenderId',
    dataTest: 'isa-sender-id-field',
    defaultValue: 'TFXH',
  },
  {
    label: 'ISA Sender ID Qualifier',
    name: 'isaSenderIdQualifier',
    dataTest: 'isa-sender-id-qualifier-field',
    defaultValue: '02',
  },
  {
    label: 'GS Receiver ID',
    name: 'gsReceiverId',
    dataTest: 'gs-receiver-id-field',
    defaultValue: '',
  },
  {
    label: 'GS Sender ID',
    name: 'gsSenderId',
    dataTest: 'GS-sender-id-field',
    defaultValue: 'TFXH',
  },
  {
    label: 'Destination Override',
    name: 'destinationOverride',
    dataTest: 'destination-override-field',
    defaultValue: '',
  },
]

const SEPARATORS_TEXT_INPUT_ITEMS = [
  {
    label: 'Segment',
    name: 'segmentSeparator',
    dataTest: 'segment-field',
    defaultValue: '',
  },
  {
    label: 'Element',
    name: 'elementSeparator',
    dataTest: 'element-field',
    defaultValue: '',
  },
  {
    label: 'Component',
    name: 'componentSeparator',
    dataTest: 'component-field',
    defaultValue: '',
  },
  {
    label: 'Repetition',
    name: 'repetitionSeparator',
    dataTest: 'repetition-field',
    defaultValue: '',
  },
]

const ENVIRONMENT_OPTIONS = [
  {
    label: 'Production',
    value: IConnectionConfigEnvironment.production,
  },
  {
    label: 'Staging',
    value: IConnectionConfigEnvironment.staging,
  },
  {
    label: 'Development',
    value: IConnectionConfigEnvironment.development,
  },
]

export const EdiOutboundConnectionConfigModalForm = () => {
  const [searchType, setSearchType] = useState('shipper')
  const [connectionType, setConnectionType] = useState<IConnectionTypes>(
    IConnectionTypes.SFTP_WITH_PRIVATE_KEY
  )
  const [environment, setEnvironment] = useState<IConnectionConfigEnvironment>(
    IConnectionConfigEnvironment.staging
  )
  const { control, errors } = useEdiOutboundConnectionConfigModalFormContext()
  const isAs2 = [connectionType, control.defaultValuesRef.current.connectionType].includes(
    IConnectionTypes.AS2
  )

  const connectionChangeHandler = useCallback(({ target }) => {
    const val = target.value

    setConnectionType(val)
  }, [])

  const environmentChangeHandler = useCallback(({ target }) => {
    const val = target.value

    setEnvironment(val)
  }, [])

  return (
    <form data-test='EdiOutboundConnectionConfigModalForm'>
      <Box mt={2} mb={4}>
        <ControlledHiddenInput name='id' control={control} />
        <ControlledSelect
          control={control}
          name='connectionType'
          defaultValue={connectionType}
          onChange={connectionChangeHandler}
          SelectProps={{
            id: 'connectionType',
            dataTest: 'connection-type-single-select',
            label: 'Connection Type',
            options: CONNECTION_TYPE_OPTIONS,
            value: connectionType,
            error: !!getErrorMessage(errors, 'connectionType'),
            validationMessage: getErrorMessage(errors, 'connectionType'),
          }}
        />
      </Box>
      <ButtonGroup
        className='mb2'
        value={searchType}
        hideLabel
        onChange={(value: string) => {
          setSearchType(value)
        }}>
        <Button value='shipper'>Shipper</Button>
        <Button value='carrier'>Carrier</Button>
      </ButtonGroup>
      {searchType === 'shipper' ? (
        <ControlledAutocompleteShipper
          control={control}
          name='company'
          AutocompleteShipperProps={{
            inputError: !!getErrorMessage(errors, 'company'),
            inputValidationMessage: getErrorMessage(errors, 'company'),
            inputIcon: 'Search',
            renderInputType: 'withIcon',
            placeholder: 'Search',
            dataTest: 'search-input',
            label: 'Shipper name',
            size: 'small',
          }}
        />
      ) : (
        <ControlledAutocompleteCarrier
          control={control}
          name='company'
          AutocompleteCarrierProps={{
            inputError: !!getErrorMessage(errors, 'company'),
            inputValidationMessage: getErrorMessage(errors, 'company'),
            inputIcon: 'Search',
            renderInputType: 'withIcon',
            placeholder: 'Search',
            dataTest: 'search-input',
            label: 'Carrier name',
            size: 'small',
          }}
        />
      )}
      <Box mt={2} mb={2}>
        <ControlledCheckbox
          name='isDisabled'
          control={control}
          defaultValue={false}
          CheckboxProps={{
            label: <Typography variant='subtitle2'>Disabled</Typography>,
          }}
        />
        <ControlledCheckbox
          name='expect997s'
          control={control}
          defaultValue
          CheckboxProps={{
            label: <Typography variant='subtitle2'>Expect 997s</Typography>,
          }}
        />
      </Box>
      <ControlledSelect
        control={control}
        name='environment'
        defaultValue={IConnectionConfigEnvironment.staging}
        onChange={environmentChangeHandler}
        SelectProps={{
          id: 'connectionType',
          dataTest: 'connection-type-single-select',
          label: 'Environment',
          options: ENVIRONMENT_OPTIONS,
          value: environment,
          error: !!getErrorMessage(errors, 'environment'),
          validationMessage: getErrorMessage(errors, 'environment'),
        }}
      />
      {isAs2 ? (
        <As2ConnectionConfigGrid includeFilenameGlob={false} control={control} errors={errors} />
      ) : (
        <FtpConnectionConfigGrid includeFilenameGlob={false} control={control} errors={errors} />
      )}
      {OUTBOUND_CORE_TEXT_INPUT_ITEMS.map(item => (
        <Box mt={2} mb={2} key={item.name}>
          <ControlledTextInput
            name={item.name}
            control={control}
            defaultValue={item.defaultValue}
            TextInputProps={{
              dataTest: item.dataTest,
              error: Boolean(
                getErrorMessage(
                  errors,
                  item.name as keyof FieldErrors<TEdiOutboundConnectionConfigModalSchema>
                )
              ),
              validationMessage: getErrorMessage(
                errors,
                item.name as keyof FieldErrors<TEdiOutboundConnectionConfigModalSchema>
              ),
              label: item.label,
            }}
          />
        </Box>
      ))}
      <Box mt={2} mb={4}>
        <ControlledSelect
          control={control}
          name='testIndicator'
          defaultValue={IUsageIndicators.TEST}
          SelectProps={{
            id: 'testIndicator',
            dataTest: 'test-indicator-select',
            label: 'Testing Indicator',
            options: TEST_INDICATOR_OPTIONS,
            error: !!getErrorMessage(errors, 'testIndicator'),
            validationMessage: getErrorMessage(errors, 'testIndicator'),
          }}
        />
      </Box>
      <Box mt={2} mb={2}>
        <Typography variant='h3'>Separators</Typography>
      </Box>
      {SEPARATORS_TEXT_INPUT_ITEMS.map(item => (
        <Box mt={2} mb={2} key={item.name}>
          <ControlledTextInput
            name={item.name}
            control={control}
            defaultValue={item.defaultValue}
            TextInputProps={{
              dataTest: item.dataTest,
              error: Boolean(
                getErrorMessage(
                  errors,
                  item.name as keyof FieldErrors<TEdiOutboundConnectionConfigModalSchema>
                )
              ),
              validationMessage: getErrorMessage(
                errors,
                item.name as keyof FieldErrors<TEdiOutboundConnectionConfigModalSchema>
              ),
              label: item.label,
            }}
          />
        </Box>
      ))}
    </form>
  )
}
